import React, { useState, useEffect } from "react";
import packageJson from "../../package.json";
import { AuthService } from "../auth/authService";
import LoginView from "../components/LoginView";
import Dashboard from "../components/Dashboard";
import ComposeView from "../components/ComposeView";
import VerificationListView from "../components/VerificationListView";
import VerificationDetailView from "../components/VerificationDetailView";
import { TemplateService } from "../services/templateService";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import VerifyEmailView from "../components/VerifyEmailView";

type AppView =
  | "loading"
  | "login"
  | "dashboard"
  | "verifyEmailView"
  | "compose"
  | "verification-list"
  | "verification-detail";

const App: React.FC = () => {
  const [view, setView] = useState<AppView>("loading");
  const [user, setUser] = useState<any>(null);
  const [selectedVerificationId, setSelectedVerificationId] = useState<number | null>(null);
  const [isCompose, setIsCompose] = useState(false);

  useEffect(() => {
    const init = async () => {
      const isAuth = await AuthService.isAuthenticated();
      if (isAuth) {
        const token = await Session.getToken();
        if (token) {
          // Verify if token is still valid on startup by doing a lightweight call
          const templateList = await TemplateService.listAll(token);
          if (templateList.status === 401) {
            RemoteLogger.warn("Token validation failed on startup (401). Logging out...");
            await AuthService.logout();
            setUser(null);
            setView("login");
            return;
          }
        } else {
          // No token in storage, treat as unauthenticated
          await AuthService.logout();
          setUser(null);
          setView("login");
          return;
        }

        const currentUser = await AuthService.getCurrentUser();
        setUser(currentUser);

        // Detect if we are in Compose mode or Read mode
        const item = Office.context?.mailbox?.item;
        const composeMode = !!(
          item &&
          typeof item.subject === "object" &&
          "getAsync" in item.subject
        );
        setIsCompose(composeMode);
        setView("dashboard");
      } else {
        setView("login");
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (view === "loading" || view === "login") return;

    const handleItemChanged = () => {
      const item = Office.context?.mailbox?.item;
      if (!item) return;

      const composeMode = !!(typeof item.subject === "object" && "getAsync" in item.subject);
      setIsCompose(composeMode);
    };

    Office.context?.mailbox?.addHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);

    return () => {
      Office.context?.mailbox?.removeHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);
    };
  }, [view]);

  const handleLoginSuccess = async () => {
    const currentUser = await AuthService.getCurrentUser();


    

    setUser(currentUser);

    const item = Office.context?.mailbox?.item;
    const composeMode = !!(item && typeof item.subject === "object" && "getAsync" in item.subject);
    setIsCompose(composeMode);
    setView("dashboard");
  };

  const handleLogout = async () => {
    await AuthService.logout();
    setUser(null);
    setView("login");
  };

  const renderContent = () => {
    switch (view) {
      case "login":
        return <LoginView onSuccess={handleLoginSuccess} />;
      case "dashboard":
        return (
          <Dashboard
            user={user}
            onLogout={handleLogout}
            onRequestVerification={() => setView("verifyEmailView")}
            onViewHistory={() => setView("verification-list")}
            onPreApprove={() => setView("compose")}
            onSelect={(id) => {
              setSelectedVerificationId(id);
              setView("verification-detail");
            }}
            onAuthenticate={() => {
              RemoteLogger.info("Authenticate email clicked");
            }}
            isCompose={isCompose}
          />
        );

      case "verifyEmailView":
        return (
          <VerifyEmailView
            onBack={() => setView("dashboard")}
            user={user}
            onLogout={handleLogout}
            onViewHistory={() => setView("verification-list")}
          />
        );
      case "compose":
        return (
          <ComposeView
            onBack={() => setView("dashboard")}
            user={user}
            onLogout={handleLogout}
            onViewHistory={() => setView("verification-list")}
          />
        );
      case "verification-list":
        return (
          <VerificationListView
            onBack={() => setView("dashboard")}
            onLogout={handleLogout}
            onSelect={(id) => {
              setSelectedVerificationId(id);
              setView("verification-detail");
            }}
          />
        );
      case "verification-detail":
        return (
          <VerificationDetailView
            id={selectedVerificationId!}
            onBack={() => setView("verification-list")}
            onLogout={handleLogout}
          />
        );
      default:
        return null;
    }
  };

  if (view === "loading") {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3 text-gray-500">
        <div className="w-7 h-7 border-4 border-gray-200 border-t-[#33ccbf] rounded-full animate-spin" />
        <p className="text-sm">Loading truApp...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <div className="flex-1">{renderContent()}</div>

      <footer className="py-4 bg-white border-t border-gray-100 mt-auto">
        <p className="text-[9px] text-gray-400 text-center uppercase tracking-wider font-medium">

          gettru adds a human verification layer to your email communications
        </p>
        <p className="text-[10px] text-gray-300 text-center mt-1">v{packageJson.version}</p>
      </footer>
    </div>
  );
};

export default App;
